import Lottie from 'lottie-react';
import { useEffect, useMemo, useState } from 'react';
import { StepContentProps } from './StepContentProps';

const LOADING_ANIMATION_CONFIG = {
  initialDelay: 1000, // Delay before starting first item
  minDuration: 2000, // Minimum time per step
  maxDuration: 4000, // Maximum time per step
  animationStartDelay: 200, // Delay before circle fill animation starts
  animationEndBuffer: 400, // Buffer before animation completes (startDelay + endBuffer)
  scrollTransitionDuration: 500, // Duration of scroll animation in ms
  itemHeight: 34, // Height of each item in pixels
  visibleHeight: 102, // Container height in pixels
  completeDelay: 600, // Delay after last item completes before onNext
  iconTransitionDuration: 300, // Duration of icon morph animation in ms
  completeStateDelay: 300, // Delay to show completed state before scrolling
};

const Step2LoadingItem = ({
  status,
  text,
  duration,
}: {
  status: 'completed' | 'loading' | 'not_started';
  text: string;
  duration?: number;
}) => {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative h-5 w-5">
        {/* Completed state */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-5 w-5 transition-all duration-300"
          preserveAspectRatio="xMidYMid meet"
          style={{
            opacity: status === 'completed' ? 1 : 0,
            transform: status === 'completed' ? 'scale(1)' : 'scale(0.8)',
          }}
        >
          <g clipPath="url(#clip0_32_6036)">
            <circle opacity="0.1" cx="10" cy="10" r="10" fill="#16BC61" />
            <path
              d="M5.91663 10.29L8.24996 12.6234L14.0833 6.79004"
              stroke="#16BC61"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_32_6036">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>

        {/* Loading state */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-5 w-5 transition-all duration-300"
          preserveAspectRatio="xMidYMid meet"
          style={{
            opacity: status === 'loading' ? 1 : 0,
            transform: status === 'loading' ? 'scale(1)' : 'scale(0.8)',
          }}
          key={status === 'loading' ? duration : 'not-loading'}
        >
          <circle
            cx="10"
            cy="10"
            r="8.5"
            fill="none"
            stroke="#E4E7EC"
            strokeWidth="3"
          />
          <circle
            cx="10"
            cy="10"
            r="8.5"
            fill="none"
            stroke="#16BC61"
            strokeWidth="3"
            strokeDasharray="53.4"
            strokeDashoffset="53.4"
            strokeLinecap="round"
            transform="rotate(-90 10 10)"
            style={{
              animation:
                status === 'loading'
                  ? `fillCircle ${duration || 3000}ms linear ${LOADING_ANIMATION_CONFIG.animationStartDelay}ms forwards`
                  : 'none',
            }}
          />
          <style>
            {`
              @keyframes fillCircle {
                from {
                  stroke-dashoffset: 53.4;
                }
                to {
                  stroke-dashoffset: 0;
                }
              }
            `}
          </style>
        </svg>

        {/* Not started state */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-5 w-5 transition-all duration-300"
          preserveAspectRatio="xMidYMid meet"
          style={{
            opacity: status === 'not_started' ? 1 : 0,
            transform: status === 'not_started' ? 'scale(1)' : 'scale(0.8)',
          }}
        >
          <g clipPath="url(#clip0_32_6084)">
            <circle cx="10" cy="9.70703" r="2" fill="#CED2DA" />
          </g>
          <defs>
            <clipPath id="clip0_32_6084">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <p
        className={`text-left text-sm sm:text-base ${
          status === 'not_started' ? 'text-[#637083]' : 'text-[#141c25]'
        }`}
      >
        {text}
      </p>
    </div>
  );
};

export const OnboardingStep2 = ({
  data,
  onNext,
  onBack: _onBack,
  isLoading: _isLoading = false,
}: StepContentProps) => {
  const [starsAnimation, setStarsAnimation] = useState<any>(null);
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(-1);
  const [currentDuration, setCurrentDuration] = useState(3000);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [imageScrollPosition, setImageScrollPosition] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    import('../../assets/lotties/star-magic.json').then((module) =>
      setStarsAnimation(module.default),
    );
  }, []);

  const url = useMemo(() => {
    let url: URL | undefined;
    try {
      url = new URL(
        data.websiteUrl.startsWith('http')
          ? data.websiteUrl
          : `https://${data.websiteUrl}`,
      );
    } catch {
      console.error('Invalid URL', data.websiteUrl);
    }
    return url;
  }, [data.websiteUrl]);

  const Step2LoadingsList = useMemo(() => {
    return [
      ...(url ? [`Connecting to ${url.hostname}`] : ['Connecting to website']),
      'Scraping your website content',
      'Extracting brand assets (logos, images)',
      'Analyzing brand colors & style',
      'Understanding your brand tone of voice',
      'Identifying key products & services',
      'Reviewing your website structure',
      'Analyzing target audience & messaging',
      'Refining brand understanding',
      'Preparing your brand engine',
      'Setting up your SuperFunnel workspace',
    ];
  }, [url]);

  useEffect(() => {
    // Start after initial delay
    const initialTimeout = setTimeout(() => {
      setCurrentLoadingIndex(0);
    }, LOADING_ANIMATION_CONFIG.initialDelay);

    return () => clearTimeout(initialTimeout);
  }, []);

  // Animate image scroll
  useEffect(() => {
    if (currentLoadingIndex < 0 || !imageLoaded) return;

    let isPaused = false;

    const scrollInterval = setInterval(() => {
      if (isPaused) return;

      setImageScrollPosition((prev) => {
        // Scroll through the image from top (0%) to bottom (100%)
        const newPosition = prev + 0.5;
        if (newPosition > 100) {
          // Reset to top first
          setImageScrollPosition(0);
          // Pause for 500ms before continuing
          isPaused = true;
          setTimeout(() => {
            isPaused = false;
          }, 500);
          return 0;
        }
        return newPosition;
      });
    }, 100);

    return () => clearInterval(scrollInterval);
  }, [currentLoadingIndex, imageLoaded]);

  useEffect(() => {
    if (
      currentLoadingIndex >= 0 &&
      currentLoadingIndex < Step2LoadingsList.length
    ) {
      // Random time between min and max duration
      const randomDelay =
        Math.floor(
          Math.random() *
            (LOADING_ANIMATION_CONFIG.maxDuration -
              LOADING_ANIMATION_CONFIG.minDuration),
        ) + LOADING_ANIMATION_CONFIG.minDuration;
      setCurrentDuration(randomDelay);

      // Scroll immediately when loading starts (except for first 2 items)
      if (currentLoadingIndex >= 1) {
        setScrollOffset(currentLoadingIndex - 1);
      }

      // Move to next item after animation completes + delay to show completed state
      const nextTimeout = setTimeout(() => {
        setCurrentLoadingIndex((prev) => prev + 1);
      }, randomDelay + LOADING_ANIMATION_CONFIG.completeStateDelay);

      return () => {
        clearTimeout(nextTimeout);
      };
    } else if (currentLoadingIndex === Step2LoadingsList.length) {
      // Wait before triggering onNext
      const timeout = setTimeout(() => {
        onNext();
      }, LOADING_ANIMATION_CONFIG.completeDelay);

      return () => clearTimeout(timeout);
    }
  }, [currentLoadingIndex, Step2LoadingsList.length, onNext]);

  // Calculate which items to show (all items for smooth scrolling)
  const getVisibleItems = () => {
    return Step2LoadingsList.map((item, index) => {
      let status: 'completed' | 'loading' | 'not_started';

      if (index < currentLoadingIndex) {
        status = 'completed';
      } else if (index === currentLoadingIndex) {
        status = 'loading';
      } else {
        status = 'not_started';
      }

      return {
        text: item,
        status,
        originalIndex: index,
      };
    });
  };
  const visibleItems = getVisibleItems();

  return (
    <div className="flex w-full flex-col gap-4 px-5 pt-5 pb-6">
      <div className="relative flex flex-col gap-2 overflow-hidden rounded-[20px] bg-linear-to-b from-[#f2f4f7] to-gray-50 p-1">
        <div className="flex items-center justify-between px-2.5 pt-1.5 pb-0.5">
          <div className="flex items-center gap-[7px]">
            <svg
              width="9"
              height="9"
              viewBox="0 0 9 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <circle cx="4.5" cy="4.5" r="4.5" fill="#FA6857" />
            </svg>
            <svg
              width="9"
              height="9"
              viewBox="0 0 9 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <circle cx="4.5" cy="4.5" r="4.5" fill="#FBBD3F" />
            </svg>
            <svg
              width="9"
              height="9"
              viewBox="0 0 9 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <circle cx="4.5" cy="4.5" r="4.5" fill="#1FC747" />
            </svg>
          </div>
          <div className="absolute inset-x-0 flex w-full flex-col items-center gap-2.5 rounded-[5px] px-2 pt-0.5 pb-[3px]">
            <p className="text-left text-[9px] text-[#637083]">
              {url?.hostname || ''}
            </p>
          </div>
          <div className="flex items-center gap-[7px] opacity-0">
            <div />
            <div />
            <div />
          </div>
        </div>
        <div className="relative aspect-video w-full overflow-hidden rounded-br-[22px] rounded-bl-[22px]">
          <div className="relative aspect-video w-full overflow-hidden rounded-br-[22px] rounded-bl-[22px]">
            <img
              src={data.screenshotUrl}
              className="absolute top-0 left-0 h-auto w-full object-cover object-top transition-all duration-300 ease-linear"
              alt="Website preview"
              style={{
                transform: `translateY(-${imageScrollPosition}%)`,
              }}
              onLoad={() => setImageLoaded(true)}
            />
          </div>
        </div>
        <div
          className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-xl border border-white/80 px-4 py-2"
          style={{
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.72) 100%)',
            backdropFilter: 'blur(5px)',
            boxShadow:
              '-3px -3px 4px 0px #FFFFFF99 inset, 3px 3px 10px 0px #5847CE0F',
          }}
        >
          <div className="size-9">
            {starsAnimation && (
              <Lottie
                animationData={starsAnimation}
                loop={true}
                className="size-9"
              />
            )}
          </div>

          <p className="text-left text-base font-medium whitespace-nowrap text-[#141c25] sm:text-xl">
            AI Agent is working...
          </p>
        </div>
      </div>
      <div
        className="relative overflow-hidden"
        style={{ height: `${LOADING_ANIMATION_CONFIG.visibleHeight}px` }}
      >
        <div
          className="flex flex-col gap-2.5 transition-transform ease-in-out"
          style={{
            transform: `translateY(-${(scrollOffset === 0 ? 0 : Math.min(scrollOffset, Step2LoadingsList.length - 3)) * LOADING_ANIMATION_CONFIG.itemHeight}px)`,
            transitionDuration: `${LOADING_ANIMATION_CONFIG.scrollTransitionDuration}ms`,
          }}
        >
          {visibleItems.map((item) => (
            <Step2LoadingItem
              key={item.originalIndex}
              status={item.status}
              text={item.text}
              duration={item.status === 'loading' ? currentDuration : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
